import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import { db } from "../../lib/firebase";
import React, { useEffect, useState, useRef, useMemo } from "react";
import blog from "./Blog.module.css";
import { Eye, Funnel, InstagramLogo, SortAscending, SortDescending } from "phosphor-react";
import { FlightBar } from "../../components/flightbar/FlightBar";
import { sampleFlight } from "../../data/flights";
import { PostCard } from "../../components/blog/PostCard";
import fm from 'front-matter';
import { Link } from 'react-router-dom';

function Blog() {
	const [, setViewCount] = useState(0);
	const hasRun = useRef(false);
	const [posts, setPosts] = useState([]);
	const [activeTag, setActiveTag] = useState(null);
	const [sortBy, setSortBy] = useState("date"); // "date" | "views"

	// Get all unique tags
	const allTags = useMemo(() => {
		return Array.from(new Set(posts.flatMap(post => post.tags || []))).sort();
	}, [posts]);

	// Filter + Sort logic
	const filteredPosts = useMemo(() => {
		let filtered = activeTag
			? posts.filter(post => post.tags?.includes(activeTag))
			: posts;

		return filtered.sort((a, b) => {
			if (sortBy === "views") {
				return (b.views || 0) - (a.views || 0);
			}
			if (sortBy === "date") {
				return new Date(b.date) - new Date(a.date); // newest first
			}
			if (sortBy === "oldest") {
				return new Date(a.date) - new Date(b.date); // newest first
			}
			return 0;
		});
	}, [posts, activeTag, sortBy]);

	// View count for the blog page (not individual posts)
	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const blogRef = doc(db, "stats", "blog");

		const incrementViews = async () => {
			await updateDoc(blogRef, {
				views: increment(1),
			});
		};

		const fetchViews = async () => {
			const snap = await getDoc(blogRef);
			if (snap.exists()) {
				setViewCount(snap.data().views);
			}
		};

		(async () => {
			await incrementViews();
			await fetchViews();
		})();
	}, []);

	// Load .md posts and fetch view count per slug
	useEffect(() => {
		fetch('/posts/00_postsIndex.json')
			.then((res) => res.json())
			.then((filenames) =>
				Promise.all(
					filenames.map((filename) =>
						fetch(`/posts/${filename}`)
							.then((res) => res.text())
							.then((text) => {
								const { attributes, body } = fm(text);
								return {
									...attributes,
									body,
									slug: filename.replace('.md', ''),
								};
							})
					)
				)
			)
			.then(async (parsedPosts) => {
				const postsWithViews = await Promise.all(
					parsedPosts.map(async (post) => {
						const ref = doc(db, "views", post.slug);
						const snap = await getDoc(ref);
						const views = snap.exists() ? snap.data().views : 0;
						return { ...post, views };
					})
				);
				setPosts(postsWithViews);
			});
	}, []);

	return (
		<main id={blog.main}>
			{/* Header */}
			<div className={blog.header}>
				<a href="https://www.instagram.com/builtbykian/" target="_blank" rel="noreferrer" className={blog.IG}>
					<InstagramLogo weight="thin" className={blog.social} />
					<p>@builtbykian</p>
				</a>
				<h1>JOURNAL</h1>
				<div className={blog.spotify}>
					<img src="/assets/blog/Spotify.png" alt="" loading="lazy"/>
				</div>
			</div>

			<FlightBar flight={sampleFlight} />

			<div className={blog.Content}>
				<div className={blog.options}>
					{/* Filter Section */}
					<div className={blog.filter}>
						<div className={blog.title}>
							<Funnel weight="regular" className={blog.filterIcon} />
							<h2>Filter</h2>
						</div>
						<div className={blog.tagContainer}>
							{allTags.map(tag => (
								<button
									key={tag}
									onClick={() => setActiveTag(activeTag === tag ? null : tag)}
									className={blog.tag}
								>
									<input
										type="checkbox"
										readOnly
										checked={activeTag === tag}
										className={blog.checkbox}
									/>
									<span>{tag}</span>
								</button>
							))}
						</div>
					</div>

					{/* Sort Section */}
					<div className={blog.sortToggle}>
                        <button onClick={() => {
                            setSortBy(prev => {
                            if (prev === 'date') return 'oldest';
                            if (prev === 'oldest') return 'views';
                            return 'date';
                            });
                        }} className={`${blog.sortButton} ${blog[sortBy]}`}>
                            <span>
                            {sortBy === 'date' && 'Newest'}
                            {sortBy === 'oldest' && 'Oldest'}
                            {sortBy === 'views' && 'Most Viewed'}
                            </span>
                            {sortBy === 'date' && <SortDescending className={blog.sortIcon} />}
                            {sortBy === 'oldest' && <SortAscending className={blog.sortIcon} />}
                            {sortBy === 'views' && <Eye className={blog.sortIcon} />}
                        </button>
                    </div>


				</div>

				{/* Posts */}
				<div className={blog.postGrid}>
					{filteredPosts.map((post, index) => (
						<Link key={index} to={`/blog/${post.slug}`}>
							<PostCard post={post} />
						</Link>
					))}
				</div>
			</div>
		</main>
	);
}

export default Blog;